import React, { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';

const Label = ({ children, tooltip }) => (
    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {children}
        {tooltip && <span title={tooltip} style={{ cursor: 'help', color: '#9ca3af', fontSize: 13 }}>ℹ</span>}
    </label>
);
const Hint = ({ children }) => <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>{children}</p>;
const Card = ({ title, icon, desc, children }) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 24, marginBottom: 20 }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{icon}</span> {title}
        </p>
        {desc && <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>{desc}</p>}
        {!desc && <div style={{ marginBottom: 20 }}/>}
        {children}
    </div>
);

function TagInput({ value = [], onChange, placeholder, color = '#1565c0', bg = '#eff6ff' }) {
    const [input, setInput] = useState('');
    const add = () => {
        const v = input.trim();
        if (!v || value.includes(v)) return;
        onChange([...value, v]);
        setInput('');
    };
    return (
        <div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {value.map((v, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: bg, borderRadius: 20, fontSize: 12, color, fontWeight: 600, border: `1px solid ${color}30` }}>
                        {v}
                        <button onClick={() => onChange(value.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
                    </span>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <input value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && add()}
                    placeholder={placeholder}
                    style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}/>
                <button onClick={add}
                    style={{ padding: '8px 16px', background: color, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    Add
                </button>
            </div>
        </div>
    );
}

export default function TenantSettings() {
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [form,    setForm]    = useState({
        inbound_block_keywords:  [],
        profanity_list:          [],
        outbound_block_keywords: [],
        competitor_names:        [],
        custom_rules:            [],
        frustration_threshold:   null,
        max_response_length:     null,
        block_response:          '',
    });

    const token = localStorage.getItem('token');
    const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        (async () => {
            try {
                const res  = await fetch(`${url}/guardrails/tenant`, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                if (data.success) setForm(p => ({ ...p, ...data.data }));
            } catch (e) { toast.error('Failed to load settings'); }
            setLoading(false);
        })();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                inbound_block_keywords:  form.inbound_block_keywords,
                profanity_list:          form.profanity_list,
                outbound_block_keywords: form.outbound_block_keywords,
                competitor_names:        form.competitor_names,
                custom_rules:            form.custom_rules,
                frustration_threshold:   form.frustration_threshold,
                max_response_length:     form.max_response_length,
                block_response:          form.block_response,
            };
            const res  = await fetch(`${url}/guardrails/tenant`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (data.success) toast.success('Settings saved ✅');
            else toast.error(data.error);
        } catch (e) { toast.error('Save failed'); }
        setSaving(false);
    };

    if (loading) return (
        <WrapperComponent>
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
                <div className="spinner-border text-primary"></div>
            </div>
        </WrapperComponent>
    );

    return (
        <WrapperComponent>
            <div style={{ maxWidth: 800, margin: '0 auto', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
                    <div>
                        <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Organisation Settings</h5>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                            Rules that apply to all your agents. Overrides platform defaults where specified.
                        </p>
                    </div>
                    <button onClick={save} disabled={saving}
                        style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #1565c0, #1e40af)', color: '#fff', border: 'none', borderRadius: 12, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1 }}>
                        {saving ? <><div className="spinner-border spinner-border-sm text-light" style={{ width: '1rem', height: '1rem' }}></div> Saving...</> : <><i className="bi bi-check-lg"></i> Save Changes</>}
                    </button>
                </div>

                {/* Info banner */}
                <div style={{ padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginBottom: 24, display: 'flex', gap: 10 }}>
                    <i className="bi bi-info-circle-fill" style={{ color: '#1565c0', fontSize: 16, flexShrink: 0, marginTop: 1 }}></i>
                    <p style={{ margin: 0, fontSize: 12, color: '#1565c0', lineHeight: 1.6 }}>
                        These settings apply to ALL agents in your organisation. Platform-level rules (set by admin) also apply and cannot be overridden.
                        Agent-specific rules can be set in each agent's configuration.
                    </p>
                </div>

                {/* Competitor Protection */}
                <Card title="Competitor Protection" icon="🛡️"
                    desc="Prevent agents from mentioning competitor brands or products in responses.">
                    <Label tooltip="If agent response mentions any of these names — response will be modified to remove them">
                        Competitor Names / Brands
                    </Label>
                    <TagInput value={form.competitor_names} onChange={v => set('competitor_names', v)}
                        placeholder="e.g. CompetitorXYZ, RivalBank" color="#7c3aed" bg="#faf5ff"/>
                    <Hint>Agent will never mention these names in responses. Case-insensitive matching.</Hint>
                </Card>

                {/* Content Controls */}
                <Card title="Content Controls" icon="📝"
                    desc="Control what content your agents can and cannot share.">
                    <div style={{ marginBottom: 20 }}>
                        <Label tooltip="Additional words your agents should never say in responses">
                            Outbound Block Keywords
                        </Label>
                        <TagInput value={form.outbound_block_keywords} onChange={v => set('outbound_block_keywords', v)}
                            placeholder="e.g. guaranteed, promise, confirmed approval" color="#dc2626" bg="#fef2f2"/>
                        <Hint>Agent responses containing these words will be replaced with fallback. Useful for compliance (e.g. never say "guaranteed").</Hint>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                        <Label tooltip="Additional words users should not send to your agents">
                            Inbound Block Keywords
                        </Label>
                        <TagInput value={form.inbound_block_keywords} onChange={v => set('inbound_block_keywords', v)}
                            placeholder="e.g. specific blocked topics" color="#d97706" bg="#fffbeb"/>
                        <Hint>Messages from users containing these words will be blocked before agent processes them.</Hint>
                    </div>
                </Card>

                {/* Additional Profanity */}
                <Card title="Profanity Filter" icon="🚫"
                    desc="Platform already filters common profanity. Add organisation-specific words here.">
                    <TagInput value={form.profanity_list} onChange={v => set('profanity_list', v)}
                        placeholder="Add word and press Enter" color="#dc2626" bg="#fef2f2"/>
                    <Hint>Platform defaults already block common profanity. Only add organisation-specific or industry-specific words.</Hint>
                </Card>

                {/* Custom Rules */}
                <Card title="Custom Compliance Rules" icon="⚖️"
                    desc="Plain English rules enforced by AI on every agent response.">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                        {form.custom_rules.map((rule, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                                <i className="bi bi-shield-check" style={{ color: '#1565c0', fontSize: 13, flexShrink: 0 }}></i>
                                <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{rule}</span>
                                <button onClick={() => set('custom_rules', form.custom_rules.filter((_, j) => j !== i))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px 6px' }}>
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input id="custom_rule_input"
                            placeholder="e.g. Never commit to specific timelines without checking with operations team"
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    const v = e.target.value.trim();
                                    if (v) { set('custom_rules', [...form.custom_rules, v]); e.target.value = ''; }
                                }
                            }}
                            style={{ flex: 1, padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}/>
                        <button onClick={() => {
                            const input = document.getElementById('custom_rule_input');
                            const v = input.value.trim();
                            if (v) { set('custom_rules', [...form.custom_rules, v]); input.value = ''; }
                        }}
                            style={{ padding: '10px 16px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            Add Rule
                        </button>
                    </div>
                    <Hint>These rules are checked by AI on every response. Use for complex compliance requirements specific to your industry.</Hint>
                </Card>

                {/* Escalation Settings */}
                <Card title="Escalation Settings" icon="⚡"
                    desc="Configure when agents should automatically escalate to human agents.">
                    <div style={{ marginBottom: 20 }}>
                        <Label tooltip="Override platform default. Leave empty to use platform setting.">
                            Frustration Threshold (override)
                        </Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <input type="number" min={1} max={10}
                                value={form.frustration_threshold || ''}
                                onChange={e => set('frustration_threshold', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder="Leave empty to use platform default"
                                style={{ width: 80, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none' }}/>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>messages before auto-escalation</span>
                        </div>
                    </div>
                    <div>
                        <Label tooltip="Custom message shown when user message is blocked by your rules">
                            Custom Block Response
                        </Label>
                        <input value={form.block_response || ''} onChange={e => set('block_response', e.target.value)}
                            placeholder="Leave empty to use platform default"
                            style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}/>
                    </div>
                </Card>

            </div>
            <ToastContainer/>
        </WrapperComponent>
    );
}
